import React from 'react';
import { Position } from '@xyflow/react';
import { 
  FiBriefcase, FiFileText, FiEdit3, FiCreditCard, FiShield, 
  FiUsers, FiUserPlus, FiUser, FiMonitor, FiSmartphone, FiServer, FiHardDrive, FiWifi, FiCpu
} from 'react-icons/fi';

export const getInitialNodes = (t) => {
  const nodes = [];

  // ==========================================
  // LEFT SIDEBAR (x: 0, w: 260)
  // ==========================================
  nodes.push({
    id: 'group-ext', type: 'groupNode', position: { x: 0, y: 0 },
    data: { width: 260, height: 2000, className: 'arch-sidebar left border-blue', title: t('architecture_diagram.sidebar_left') }
  });

  const extItems = [
    { id: 'ext-hospital', icon: <FiBriefcase/>, key: 'hospital', class: 'text-blue' },
    { id: 'ext-einvoice', icon: <FiFileText/>, key: 'einvoice', class: 'text-blue' },
    { id: 'ext-esign', icon: <FiEdit3/>, key: 'esign', class: 'text-blue' },
    { id: 'ext-payment', icon: <FiCreditCard/>, key: 'payment', class: 'text-red border-red' },
    { id: 'ext-bhxh', icon: <FiShield/>, key: 'bhxh', class: 'bg-blue-light text-white' },
    { id: 'ext-vnpd', icon: <FiShield/>, key: 'vnpd', class: 'bg-green-light text-white' },
    { id: 'ext-boyte', icon: <FiShield/>, key: 'boyte', class: 'bg-blue-dark text-white' },
  ];
  
  nodes.push({
    id: 'ext-top-group', type: 'groupNode', parentId: 'group-ext', position: { x: 10, y: 70 },
    data: { width: 240, height: 600, className: 'border-blue bg-white', handles: [{ type: 'source', position: Position.Right, id: 'right' }] }
  });

  nodes.push({
    id: 'ext-bot-group', type: 'groupNode', parentId: 'group-ext', position: { x: 10, y: 700 },
    data: { width: 240, height: 260, className: 'border-blue bg-white', handles: [{ type: 'source', position: Position.Right, id: 'right' }] }
  });

  extItems.forEach((item, index) => {
    if (index < 5) {
      nodes.push({
        id: item.id, type: 'itemNode', parentId: 'ext-top-group', position: { x: 10, y: 15 + index * 115 },
        data: { width: 220, height: 100, label: t(`architecture_diagram.external_orgs.${item.key}`), icon: item.icon, className: `${item.class} bold` }
      });
    } else {
      nodes.push({
        id: item.id, type: 'itemNode', parentId: 'ext-bot-group', position: { x: 10, y: 15 + (index - 5) * 125 },
        data: { width: 220, height: 100, label: t(`architecture_diagram.external_orgs.${item.key}`), icon: item.icon, className: `${item.class} bold` }
      });
    }
  });

  // ==========================================
  // RIGHT SIDEBAR (x: 1860, w: 200)
  // ==========================================
  const rightHandles = [
    { type: 'target', position: Position.Left, id: 'h-users', style: { top: 45 } }, // center of user-patient (y=10, h=70)
    { type: 'target', position: Position.Left, id: 'h-apps', style: { top: 195 } },
    { type: 'target', position: Position.Left, id: 'h-mid', style: { top: 490 } },
    { type: 'target', position: Position.Left, id: 'h-exc', style: { top: 980 } },
    { type: 'target', position: Position.Left, id: 'h-db', style: { top: 1500 } },
    { type: 'target', position: Position.Left, id: 'h-infra', style: { top: 1820 } }
  ];
  
  nodes.push({
    id: 'group-sec', type: 'groupNode', position: { x: 1860, y: 0 },
    data: { 
      width: 200, 
      height: 2000, 
      className: 'arch-sidebar right bg-orange-fade border-orange', 
      title: t('architecture_diagram.sidebar_right'), 
      titleClassName: 'security-sidebar-title text-orange',
      handles: rightHandles 
    }
  });

  // ==========================================
  // CORE AREA (x: 400, w: 1360)
  // ==========================================
  const coreX = 400;
  const colW = 420;
  const gap = 50;
  const wrapperPad = 20;
  const wrapperW = colW * 3 + gap * 2 + wrapperPad * 2; // 1360

  // 1. USERS LAYER (y: 10)
  nodes.push({
    id: 'wrap-users', type: 'groupNode', position: { x: coreX - wrapperPad, y: 10 - wrapperPad },
    data: { width: wrapperW, height: 70 + wrapperPad * 2, className: 'layer-wrapper border-blue-light' }
  });
  nodes.push(
    { id: 'user-doctor', type: 'itemNode', parentId: 'wrap-users', position: { x: wrapperPad, y: wrapperPad }, data: { width: colW, height: 70, label: t('architecture_diagram.top_bar.doctor'), icon: <FiUsers className="text-green"/>, className: 'user-box border-blue bold' } },
    { id: 'user-admin', type: 'itemNode', parentId: 'wrap-users', position: { x: wrapperPad + colW + gap, y: wrapperPad }, data: { width: colW, height: 70, label: t('architecture_diagram.top_bar.admin'), icon: <FiBriefcase className="text-blue"/>, className: 'user-box border-blue bold' } },
    { id: 'user-patient', type: 'itemNode', parentId: 'wrap-users', position: { x: wrapperPad + (colW + gap) * 2, y: wrapperPad }, data: { width: colW, height: 70, label: t('architecture_diagram.top_bar.patient'), icon: <FiUserPlus className="text-red"/>, className: 'user-box border-blue bold' } }
  );

  // 2. APPS LAYER (y: 150)
  nodes.push({
    id: 'wrap-apps', type: 'groupNode', position: { x: coreX - wrapperPad, y: 150 - wrapperPad },
    data: { width: wrapperW, height: 90 + wrapperPad * 2, className: 'layer-wrapper border-blue-light', handles: [{ type: 'target', position: Position.Right, id: 'r-target' }] }
  });
  nodes.push(
    { id: 'app-doctor', type: 'itemNode', parentId: 'wrap-apps', position: { x: wrapperPad, y: wrapperPad }, data: { width: colW * 2 + gap, height: 90, label: t('architecture_diagram.app_layer.doctor_apps'), icons: [<FiMonitor className="text-orange"/>, <FiSmartphone className="text-orange"/>], className: 'app-box border-blue-light bold', handles: [{ type: 'source', position: Position.Bottom, id: 's-exam', style: { left: 210 } }, { type: 'source', position: Position.Bottom, id: 's-mgmt', style: { left: 680 } }] } },
    { id: 'app-patient', type: 'itemNode', parentId: 'wrap-apps', position: { x: wrapperPad + (colW + gap) * 2, y: wrapperPad }, data: { width: colW, height: 90, label: t('architecture_diagram.app_layer.patient_apps'), icons: [<FiMonitor className="text-blue"/>, <FiSmartphone className="text-blue"/>], className: 'app-box border-blue-light bold' } },
  );

  // 3. MIDDLE LAYER (y: 320)
  const midH = 340;
  nodes.push({
    id: 'wrap-mid', type: 'groupNode', position: { x: coreX - wrapperPad, y: 320 - wrapperPad },
    data: { width: wrapperW, height: midH + wrapperPad * 2, className: 'layer-wrapper border-blue-light' }
  });
  nodes.push(
    { id: 'group-exam', type: 'groupNode', parentId: 'wrap-mid', position: { x: wrapperPad, y: wrapperPad }, data: { width: colW, height: midH, title: t('architecture_diagram.middle_layer.exam.title'), className: 'layer-group border-green', titleClassName: 'bg-green-light', handles: [{ type: 'target', position: Position.Left, id: 'h-mid' }] } },
    { id: 'group-mgmt', type: 'groupNode', parentId: 'wrap-mid', position: { x: wrapperPad + colW + gap, y: wrapperPad }, data: { width: colW, height: midH, title: t('architecture_diagram.middle_layer.management.title'), className: 'layer-group border-blue', titleClassName: 'bg-blue-light' } },
    { id: 'group-pat', type: 'groupNode', parentId: 'wrap-mid', position: { x: wrapperPad + (colW + gap) * 2, y: wrapperPad }, data: { width: colW, height: midH, title: t('architecture_diagram.middle_layer.patient_services.title'), className: 'layer-group border-gray', titleClassName: 'bg-gray text-blue', handles: [{ type: 'target', position: Position.Right, id: 'r-target' }] } },
  );

  const addItems = (parentId, itemsArray, cols, parentWidth, extraClass = '') => {
    const itemW = cols === 1 ? parentWidth - 40 : (parentWidth - 60) / 2;
    itemsArray.forEach((label, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      nodes.push({
        id: `${parentId}-item-${i}`, type: 'itemNode', parentId: parentId,
        position: { x: 20 + col * (itemW + 20), y: 80 + row * 60 },
        data: { width: itemW, height: 45, label, className: `sub-item ${extraClass}` }
      });
    });
  };

  addItems('group-exam', t('architecture_diagram.middle_layer.exam.items', { returnObjects: true }), 1, colW, 'border-green bold');
  addItems('group-mgmt', t('architecture_diagram.middle_layer.management.items', { returnObjects: true }), 2, colW, 'border-blue bold');
  addItems('group-pat', t('architecture_diagram.middle_layer.patient_services.items', { returnObjects: true }), 2, colW, 'border-blue bold');

  // 4. DATA EXCHANGE (y: 740) & INTEGRATION BUS (y: 1100)
  const exH = 280;
  nodes.push({
    id: 'wrap-exc', type: 'groupNode', position: { x: coreX - wrapperPad, y: 740 - wrapperPad },
    data: { width: wrapperW, height: 520, className: 'layer-wrapper border-blue-light' }
  });
  nodes.push(
    { id: 'group-byt', type: 'groupNode', parentId: 'wrap-exc', position: { x: wrapperPad, y: wrapperPad }, data: { width: colW, height: exH, title: t('architecture_diagram.data_exchange.boyte.title'), className: 'layer-group border-blue-light', titleClassName: 'text-blue', handles: [{ type: 'target', position: Position.Left, id: 'h-byt' }, { type: 'target', position: Position.Left, id: 'h-lgsp', style: { top: 235 } }] } },
    { id: 'group-other', type: 'groupNode', parentId: 'wrap-exc', position: { x: wrapperPad + colW + gap, y: wrapperPad }, data: { width: colW, height: exH, title: t('architecture_diagram.data_exchange.other_units.title'), className: 'layer-group border-blue-light', titleClassName: 'text-blue', handles: [{ type: 'target', position: Position.Left, id: 'h-oth' }] } },
    { id: 'group-int', type: 'groupNode', parentId: 'wrap-exc', position: { x: wrapperPad + (colW + gap) * 2, y: wrapperPad }, data: { width: colW, height: exH, title: t('architecture_diagram.data_exchange.internal.title'), className: 'layer-group border-blue-light', titleClassName: 'text-blue' } },
  );

  // Create a wrapper for the databases
  nodes.push({
    id: 'group-byt-dbs', type: 'groupNode', parentId: 'group-byt',
    position: { x: 15, y: 50 },
    data: { width: colW - 30, height: 130, className: 'border-dashed border-gray border-2 bg-transparent', handles: [{ type: 'source', position: Position.Bottom, id: 's-bot' }] }
  });

  // Manually add items to group-byt-dbs to control Y position
  const bytItems = t('architecture_diagram.data_exchange.boyte.items', { returnObjects: true });
  const bItemW = (colW - 70) / 2;
  bytItems.forEach((label, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    nodes.push({
      id: `group-byt-item-${i}`, type: 'itemNode', parentId: 'group-byt-dbs',
      position: { x: 10 + col * (bItemW + 20), y: 15 + row * 60 },
      data: { width: bItemW, height: 45, label, className: `sub-item border-blue bold` }
    });
  });
  addItems('group-other', t('architecture_diagram.data_exchange.other_units.items', { returnObjects: true }), 2, colW, 'border-blue bold');
  addItems('group-int', t('architecture_diagram.data_exchange.internal.items', { returnObjects: true }), 2, colW, 'border-blue bold');

  nodes.push({
    id: 'byt-lgsp', type: 'itemNode', parentId: 'group-byt',
    position: { x: 20, y: 210 },
    data: { width: colW - 40, height: 50, label: t('architecture_diagram.data_exchange.boyte.lgsp'), className: 'bg-orange text-white bold', handles: [{ type: 'target', position: Position.Top, id: 't-top' }] }
  });

  // 5. INTEGRATION BUS (now inside wrap-exc, absolute y: 1100 -> relative y: 380)
  nodes.push({
    id: 'group-bus', type: 'groupNode', parentId: 'wrap-exc', position: { x: wrapperPad, y: 380 },
    data: { width: 1360, height: 120, title: t('architecture_diagram.integration_bus.title'), className: 'bus-group border-orange', titleClassName: 'text-orange bg-orange-fade' }
  });
  const busItems = t('architecture_diagram.integration_bus.items', { returnObjects: true });
  busItems.forEach((label, i) => {
    nodes.push({
      id: `bus-item-${i}`, type: 'itemNode', parentId: 'group-bus',
      position: { x: 40 + i * (1320 / busItems.length), y: 65 },
      data: { width: 260, height: 40, label, className: 'outline-orange' }
    });
  });

  // 6. DATABASE LAYER (y: 1300)
  const dbH = 400;
  nodes.push(
    { id: 'group-db-int', type: 'groupNode', position: { x: coreX, y: 1300 }, data: { width: colW + 60, height: dbH, title: t('architecture_diagram.database_layer.internal_db.title'), className: 'db-group bg-green-fade border-green', titleClassName: 'text-green' } },
    { id: 'group-db-wh', type: 'groupNode', position: { x: coreX + colW + 90, y: 1300 }, data: { width: colW + 60, height: dbH, title: t('architecture_diagram.database_layer.data_warehouse.title'), className: 'db-group bg-blue-fade border-blue', titleClassName: 'text-blue' } },
    { id: 'group-db-ext', type: 'groupNode', position: { x: coreX + (colW * 2) + 180, y: 1300 }, data: { width: colW - 180, height: dbH, title: t('architecture_diagram.database_layer.external_db.title'), className: 'db-group bg-gray border-gray', titleClassName: 'text-gray', handles: [{ type: 'target', position: Position.Right, id: 'r-target' }] } },
  );

  // Sub-headers for CSDL Noi Bo
  nodes.push(
    { id: 'db-int-head-exam', type: 'itemNode', parentId: 'group-db-int', position: { x: 20, y: 60 }, data: { width: 210, height: 40, label: t('architecture_diagram.database_layer.internal_db.exam.title'), className: 'font-bold bg-transparent no-border text-center' } },
    { id: 'db-int-head-mgmt', type: 'itemNode', parentId: 'group-db-int', position: { x: 250, y: 60 }, data: { width: 210, height: 40, label: t('architecture_diagram.database_layer.internal_db.management.title'), className: 'font-bold bg-transparent no-border text-center' } }
  );

  const examDbItems = t('architecture_diagram.database_layer.internal_db.exam.items', { returnObjects: true });
  examDbItems.forEach((label, i) => {
    nodes.push({ id: `db-int-exam-${i}`, type: 'itemNode', parentId: 'group-db-int', position: { x: 20, y: 110 + i * 45 }, data: { width: 210, height: 35, label, className: 'db-item border-green bg-white' } });
  });

  const mgmtDbItems = t('architecture_diagram.database_layer.internal_db.management.items', { returnObjects: true });
  mgmtDbItems.forEach((label, i) => {
    nodes.push({ id: `db-int-mgmt-${i}`, type: 'itemNode', parentId: 'group-db-int', position: { x: 250, y: 110 + i * 45 }, data: { width: 210, height: 35, label, className: 'db-item border-green bg-white' } });
  });

  const addDbItems = (parentId, itemsArray, cols, parentWidth, startY = 80) => {
    const itemW = cols === 1 ? parentWidth - 40 : (parentWidth - 60) / 2;
    itemsArray.forEach((label, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      nodes.push({
        id: `${parentId}-item-${i}`, type: 'itemNode', parentId: parentId,
        position: { x: 20 + col * (itemW + 20), y: startY + row * 45 },
        data: { width: itemW, height: 35, label, className: 'db-item border-green bg-white' }
      });
    });
  };

  // 6a. Dữ liệu phân tích
  nodes.push({
    id: 'group-db-wh-analytics', type: 'groupNode', parentId: 'group-db-wh', position: { x: 10, y: 50 },
    data: { width: colW + 40, height: 140, title: t('architecture_diagram.database_layer.data_warehouse.analysis.title'), className: 'layer-group border-white bg-transparent border-2', titleClassName: 'text-gray-dark font-bold bg-transparent' }
  });

  const analysisItems = t('architecture_diagram.database_layer.data_warehouse.analysis.items', { returnObjects: true });
  const aItemW = (colW + 40 - 30) / 2;
  analysisItems.forEach((label, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    nodes.push({ id: `db-wh-analysis-${i}`, type: 'itemNode', parentId: 'group-db-wh-analytics', position: { x: 10 + col * (aItemW + 10), y: 45 + row * 40 }, data: { width: aItemW, height: 35, label, className: 'db-item border-blue bg-white font-bold' } });
  });

  // 6b. Dữ liệu lưu trữ
  nodes.push({
    id: 'group-db-wh-storage', type: 'groupNode', parentId: 'group-db-wh', position: { x: 10, y: 200 },
    data: { width: colW + 40, height: 190, title: t('architecture_diagram.database_layer.data_warehouse.storage.title'), className: 'layer-group border-white bg-transparent border-2', titleClassName: 'text-gray-dark font-bold bg-transparent' }
  });

  const storageItems = t('architecture_diagram.database_layer.data_warehouse.storage.items', { returnObjects: true });
  storageItems.forEach((label, i) => {
    nodes.push({ id: `db-wh-storage-${i}`, type: 'itemNode', parentId: 'group-db-wh-storage', position: { x: 10 + i * (aItemW + 10), y: 45 }, data: { width: aItemW, height: 35, label, className: 'db-item border-blue bg-white font-bold' } });
  });

  // Cylinder block "KHO DỮ LIỆU"
  nodes.push({
    id: 'db-wh-cylinder', type: 'itemNode', parentId: 'group-db-wh-storage', position: { x: 10, y: 110 },
    data: { width: 440, height: 60, label: t('architecture_diagram.database_layer.data_warehouse.storage.cylinder'), className: 'db-wh-cylinder' }
  });
  // CSDL Phục vụ bên ngoài
  nodes.push({
    id: 'group-db-ext-query', type: 'groupNode', parentId: 'group-db-ext', position: { x: 10, y: 50 },
    data: { width: colW - 180 - 20, height: 250, title: t('architecture_diagram.database_layer.external_db.query.title'), className: 'layer-group border-white bg-transparent border-2', titleClassName: 'text-gray-dark font-bold bg-transparent' }
  });
  addDbItems('group-db-ext-query', t('architecture_diagram.database_layer.external_db.query.items', { returnObjects: true }), 1, colW - 180 - 20, 50);

  // 7. INFRASTRUCTURE LAYER (y: 1750)
  const infraY = 1750;
  const infraW = (1360 - 4 * 20 - 40) / 5; // Total group width is 1360. Margin 20px sides. 5 items -> 4 gaps of 20px
  
  nodes.push({
    id: 'group-infra', type: 'groupNode', position: { x: coreX, y: infraY },
    data: { width: 1360, height: 160, className: 'infra-group bg-gray border-gray' }
  });

  nodes.push(
    { id: 'infra-server', type: 'itemNode', parentId: 'group-infra', position: { x: 20, y: 40 }, data: { width: infraW, height: 80, label: 'Hệ thống máy chủ', icon: <FiServer className="text-blue"/>, className: 'infra-box border-blue bold' } },
    { id: 'infra-storage', type: 'itemNode', parentId: 'group-infra', position: { x: 20 + infraW + 20, y: 40 }, data: { width: infraW, height: 80, label: 'Hệ thống lưu trữ (NAS)', icon: <FiHardDrive className="text-blue"/>, className: 'infra-box border-blue bold' } },
    { id: 'infra-network', type: 'itemNode', parentId: 'group-infra', position: { x: 20 + (infraW + 20)*2, y: 40 }, data: { width: infraW, height: 80, label: 'Hệ thống mạng', icon: <FiWifi className="text-blue"/>, className: 'infra-box border-blue bold' } },
    { id: 'infra-security', type: 'itemNode', parentId: 'group-infra', position: { x: 20 + (infraW + 20)*3, y: 40 }, data: { width: infraW, height: 80, label: 'Hệ thống Bảo mật ATTT', icon: <FiShield className="text-blue"/>, className: 'infra-box border-blue bold' } },
    { id: 'infra-peripheral', type: 'itemNode', parentId: 'group-infra', position: { x: 20 + (infraW + 20)*4, y: 40 }, data: { width: infraW, height: 80, label: 'Hệ thống ngoại vi', icon: <FiCpu className="text-blue"/>, className: 'infra-box border-blue bold' } },
  );

  return nodes;
};
